<template>
  <button
    @click="openModal"
    class="flex flex-col items-start gap-1 p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-white dark:hover:bg-gray-700 transition-all shadow-sm hover:shadow min-w-[8rem] max-w-[10rem] text-left group"
    :title="tt(`View ${deviceValue.name} history`, `Zgodovina: ${deviceValue.name}`)">
    <!-- Name + badge row -->
    <div class="flex items-center justify-between w-full gap-1">
      <span class="text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate leading-tight">
        {{ deviceValue.name || deviceValue.valueKey }}
      </span>
      <span
        class="flex-shrink-0 text-[9px] font-semibold px-1 py-0.5 rounded uppercase tracking-wide"
        :class="badgeClass"
        >{{ loggingTypeShort }}</span
      >
    </div>

    <!-- Current value -->
    <div class="flex items-baseline gap-1">
      <span class="text-lg font-bold leading-none text-gray-800 dark:text-white">{{ formattedValue }}</span>
      <span v-if="deviceValue.unit" class="text-[11px] text-gray-500 dark:text-gray-400">{{ deviceValue.unit }}</span>
    </div>

    <!-- Mini sparkline -->
    <div class="w-full h-8 rounded overflow-hidden bg-gray-100 dark:bg-gray-600/50 mt-0.5">
      <canvas ref="miniCanvasRef" class="w-full h-full"></canvas>
    </div>

    <!-- "View chart" hint on hover -->
    <div class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity self-center">
      <ControlIcon name="activity" size="0.75rem" class="text-blue-400" />
      <span class="text-[10px] text-blue-400 font-medium">{{ tt('View chart', 'Poglej graf') }}</span>
    </div>
  </button>

  <!-- ChartModal (Teleported to body so it escapes any overflow:hidden parents) -->
  <Teleport to="body">
    <ChartModal
      v-if="showModal && valueWithLogging"
      :value="valueWithLogging"
      :all-values="[valueWithLogging]"
      :chart-data="chartData"
      :loading="loading"
      :error="error"
      :selected-period="selectedPeriod"
      :periods="PERIODS"
      @close="closeModal"
      @select-value="() => {}"
      @change-period="changePeriod" />
  </Teleport>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import type { Chart as ChartInstance } from 'chart.js';
import { apiFetch } from '@/services/api';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { tt } from '../../utils/consoleText';
import { loadChartJs } from '../../utils/chartJs';
import type { DeviceValue } from '@/types';
import { toFiniteNumber } from './deviceWidgetTypes';
import type { ValueLogPoint } from './deviceWidgetTypes';

// Chart.js is only ever used by this tile's sparkline and by the modal, both
// behind an interaction — see `utils/chartJs.ts`. The modal goes with it.
const ChartModal = defineAsyncComponent(() => import('../ChartModal.vue'));

const props = defineProps<{
  deviceValue: DeviceValue;
  device: { id: string; deviceName: string };
  accentColor?: string;
}>();

// ── Periods ───────────────────────────────────────────────────────────────
const PERIODS = [
  { label: '1h', value: '1h' },
  { label: '6h', value: '6h' },
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
];

const PERIOD_MS: Record<string, number> = {
  '1h': 3_600_000,
  '6h': 21_600_000,
  '24h': 86_400_000,
  '7d': 604_800_000,
  '30d': 2_592_000_000,
};

// ── State ─────────────────────────────────────────────────────────────────
/** One logged row of the `/values/:id/history` answer for the selected period. */
interface HistoryPoint {
  timestamp: string;
  value?: number | null;
  avg?: number | null;
  min?: number | null;
  max?: number | null;
  delta?: number | null;
}

/** The history body as ChartModal's `chartData` prop takes it. */
interface HistoryPayload {
  loggingType?: string;
  unit?: string;
  decimalPlaces?: number;
  data: HistoryPoint[];
  range?: { min?: number; max?: number };
}

const showModal = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);
const selectedPeriod = ref('24h');
const chartData = ref<HistoryPayload | null>(null);
const miniData = ref<ValueLogPoint[]>([]);
const miniCanvasRef = ref<HTMLCanvasElement | null>(null);
let miniChart: ChartInstance | null = null;

// ── Computed ──────────────────────────────────────────────────────────────
const loggingType = computed(() => props.deviceValue.loggingType ?? 'Analog');

const loggingTypeShort = computed(() => {
  switch (loggingType.value) {
    case 'Counter':
      return 'cnt';
    case 'Setpoint':
      return 'spt';
    default:
      return 'log';
  }
});

const badgeClass = computed(() => {
  switch (loggingType.value) {
    case 'Counter':
      return 'bg-green-100  dark:bg-green-900/40  text-green-700  dark:text-green-300';
    case 'Setpoint':
      return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';
    default:
      return 'bg-blue-100   dark:bg-blue-900/40   text-blue-700   dark:text-blue-300';
  }
});

const chartLineColor = computed(() => {
  if (props.accentColor) return props.accentColor;
  switch (loggingType.value) {
    case 'Counter':
      return 'rgb(34, 197, 94)';
    case 'Setpoint':
      return 'rgb(168, 85, 247)';
    default:
      return 'rgb(59, 130, 246)';
  }
});

const formattedValue = computed(() => {
  const v = props.deviceValue.currentValue;
  if (v === null || v === undefined) return '—';

  // Digital (boolean) values → On/Off. Numeric values must never collapse
  // to boolean labels (e.g. position 0 must show "0", not "Off").
  if (loggingType.value === 'Digital') {
    if (v === true || v === 1 || v === '1' || v === 'on' || v === 'true') return 'On';
    if (v === false || v === 0 || v === '0' || v === 'off' || v === 'false') return 'Off';
  } else if (typeof v === 'boolean') {
    return v ? 'On' : 'Off';
  }

  if (typeof v === 'number') {
    const dp = props.deviceValue.decimalPlaces ?? (Number.isInteger(v) ? 0 : 1);
    return v.toFixed(dp);
  }
  return String(v);
});

const valueWithLogging = computed(() => ({
  id: props.deviceValue.id,
  valueName: props.deviceValue.name || props.deviceValue.valueKey,
  deviceId: props.device.id,
  deviceName: props.device.deviceName,
  loggingType: loggingType.value as 'Analog' | 'Counter' | 'Setpoint',
  unit: props.deviceValue.unit,
  decimalPlaces: props.deviceValue.decimalPlaces,
}));

// ── Mini sparkline (24h, loaded once on mount) ────────────────────────────
async function loadMiniData() {
  const end = new Date();
  const start = new Date(end.getTime() - 86_400_000); // 24h
  try {
    const resp = await apiFetch(
      `/values/${props.deviceValue.id}/history?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`,
    );
    miniData.value = resp?.data ?? [];
  } catch {
    miniData.value = [];
  }
}

async function renderMiniChart() {
  const canvas = miniCanvasRef.value;
  if (!canvas || !miniData.value.length) return;

  const { Chart } = await loadChartJs();

  // The tile may have unmounted (or been re-pointed) while the module loaded.
  if (miniCanvasRef.value !== canvas) return;

  if (miniChart) miniChart.destroy();

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const color = chartLineColor.value;
  let values: number[];

  if (loggingType.value === 'Counter') {
    values = miniData.value.map((d) => toFiniteNumber(d.delta) ?? 0);
  } else {
    values = miniData.value.map((d) => toFiniteNumber(d.avg) ?? toFiniteNumber(d.value) ?? 0);
  }

  const isSteppedLine = loggingType.value === 'Setpoint';
  const isBars = loggingType.value === 'Counter';
  const steppedMode: 'after' | undefined = isSteppedLine ? 'after' : undefined;

  miniChart = new Chart(ctx, {
    type: isBars ? 'bar' : 'line',
    data: {
      labels: values.map((_, i) => i),
      datasets: [
        {
          data: values,
          borderColor: color,
          backgroundColor: isBars
            ? color.replace('rgb(', 'rgba(').replace(')', ', 0.6)')
            : color.replace('rgb(', 'rgba(').replace(')', ', 0.15)'),
          borderWidth: isBars ? 0 : 1.5,
          fill: !isBars,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 0,
          stepped: steppedMode,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: { display: false }, y: { display: false, beginAtZero: isBars } },
    },
  });
}

watch(miniData, async () => {
  await nextTick();
  void renderMiniChart();
});

// ── Modal data fetching ───────────────────────────────────────────────────
async function fetchChartData(period: string) {
  loading.value = true;
  error.value = null;
  const ms = PERIOD_MS[period] ?? 86_400_000;
  const end = new Date();
  const start = new Date(end.getTime() - ms);
  try {
    const resp = await apiFetch(
      `/values/${props.deviceValue.id}/history?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`,
    );
    chartData.value = resp ?? null;
  } catch (__caught: unknown) {
    const e = __caught as Error;
    error.value = e?.message ?? tt('Failed to load history', 'Nalaganje zgodovine ni uspelo');
    chartData.value = null;
  } finally {
    loading.value = false;
  }
}

function openModal() {
  showModal.value = true;
  fetchChartData(selectedPeriod.value);
}

function closeModal() {
  showModal.value = false;
}

function changePeriod(period: string) {
  selectedPeriod.value = period;
  fetchChartData(period);
}

// ── Lifecycle ─────────────────────────────────────────────────────────────
onMounted(() => loadMiniData());

onUnmounted(() => {
  if (miniChart) miniChart.destroy();
});
</script>
